/**
 * Machine room setup (spec §3.2), as pure functions over the entity tree.
 *
 * This is the hinge of the whole survey: N groups ⇒ N Lobbies sections, M
 * hoistways ⇒ M Overhead + M Pit sections (brief §5), and every later question
 * is scoped to something defined here. So the editing rules live in plain
 * TypeScript that tests exercise directly, and `RoomSetupScreen.tsx` stays a
 * renderer with taps wired to these functions.
 *
 * ── Three rules this file obeys ──────────────────────────────────────────────
 *
 *  1. **Structure, never answers.** M1/M2/M3/M6/M7 are captured in
 *     `MachineRoom` (D-004). Nothing here writes through `answerKey()`; the
 *     mutators take the room out of the document and change the tree, and the
 *     screen persists that through `setStructure()`.
 *
 *  2. **The car-placement RULE is the validation engine's** (§4 rule 7 / V7).
 *     `builderView` reports who currently owns a car, which is a fact read off
 *     the tree — but "every car in exactly one group and one hoistway" is
 *     decided by `validate()` and surfaced through `assignmentIssues()` below,
 *     verbatim. This file never decides whether the room is placeable.
 *
 *  3. **Changing the room type is destructive, so it is planned first.**
 *     `typeChangePlan` lists exactly which stored answers would stop applying
 *     (G11–G17 leaving a Traction room; E14 and P6 leaving a Hydro/MRL one) so
 *     the screen can say what will be lost before anything is discarded. MRL and
 *     Hydro share a question set, so switching between them discards nothing —
 *     which falls out of `formSetFor()` rather than being special-cased.
 */

import { QUESTIONS, byId } from '../../core/catalog';
import { answerKey, formSetFor } from '../../core/types';
import type {
  CarRef,
  Group,
  Hoistway,
  MachineRoom,
  QuestionDef,
  RoomType,
  Scope,
  SurveyDocument,
  ValidationIssue,
} from '../../core/types';
import { randomId, type IdSource } from './newSurvey';
import { issuesFor, type SurveyResults } from './results';

/* ─────────────────────────────── id minting ─────────────────────────────── */

export function newCarId(newId: IdSource = randomId): string {
  return `car-${newId()}`;
}

export function newGroupId(newId: IdSource = randomId): string {
  return `grp-${newId()}`;
}

export function newHoistwayId(newId: IdSource = randomId): string {
  return `hw-${newId()}`;
}

/* ───────────────────────────── default naming ───────────────────────────── */

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * "Machine Room 2" — schema.ts requires a non-empty name from the first save,
 * so a room always has one before the surveyor types anything.
 *
 * Pass a `roomId` for a room that already exists and the number is its position,
 * which is what the M2 field falls back to when the surveyor clears the box: the
 * stored name stays schema-valid without the field fighting the backspace.
 */
export function defaultRoomName(doc: SurveyDocument, roomId?: string): string {
  const at = roomId ? doc.rooms.findIndex((room) => room.id === roomId) : -1;
  return `Machine Room ${String(at >= 0 ? at + 1 : doc.rooms.length + 1)}`;
}

/**
 * Car labels run building-wide, not per room: the second room in St. Luke's
 * starts at "Car 7". The next label is one past the highest number already used
 * anywhere in the survey, so removing Car 5 and adding a car does not mint a
 * second "Car 5" that a surveyor cannot tell apart in the group builder.
 */
export function nextCarLabel(doc: SurveyDocument): string {
  let highest = 0;
  for (const room of doc.rooms) {
    for (const car of room.cars) {
      const match = /(\d+)\s*$/.exec(car.label.trim());
      const raw = match?.[1];
      if (raw) highest = Math.max(highest, Number(raw));
    }
  }
  return `Car ${String(highest + 1)}`;
}

export function nextGroupName(room: MachineRoom): string {
  const letter = ALPHABET[room.groups.length];
  return letter ? `Group ${letter}` : `Group ${String(room.groups.length + 1)}`;
}

export function nextHoistwayName(room: MachineRoom): string {
  return `Hoistway ${String(room.hoistways.length + 1)}`;
}

/* ─────────────────────────────── room creation ──────────────────────────── */

export interface NewRoomInput {
  roomId: string;
  name: string;
  type: RoomType;
}

/** A room the moment its type is chosen: valid against the schema, and empty. */
export function newMachineRoom(input: NewRoomInput): MachineRoom {
  return { id: input.roomId, name: input.name, type: input.type, cars: [], groups: [], hoistways: [] };
}

/* ───────────────────────────── car list (M3) ────────────────────────────── */

export function addCar(room: MachineRoom, car: CarRef): void {
  room.cars.push(car);
}

/**
 * Remove a car and every reference to it.
 *
 * Leaving the id behind in a group or a hoistway is not a harmless orphan: V7
 * raises `"Group A lists car-3, which isn't one of this room's cars"`, and the
 * surveyor has no control on this screen that could clear it — the car is gone
 * from the pool. So the tree is kept referentially whole at the point of edit.
 */
export function removeCar(room: MachineRoom, carId: string): void {
  room.cars = room.cars.filter((car) => car.id !== carId);
  for (const group of room.groups) group.carIds = group.carIds.filter((id) => id !== carId);
  for (const hoistway of room.hoistways) {
    hoistway.carIds = hoistway.carIds.filter((id) => id !== carId);
  }
}

/** The per-car "on ATS" tag — DECISIONS D-003. B14 and B17 are counted from it. */
export function setCarOnAts(room: MachineRoom, carId: string, onAts: boolean): void {
  const car = room.cars.find((candidate) => candidate.id === carId);
  if (car) car.onAts = onAts;
}

/* ──────────────────── the group / hoistway builder (M6, M7) ─────────────── */

export type BucketKind = 'group' | 'hoistway';

/** Groups and hoistways differ in one field (landings); everything else is shared. */
interface Bucket {
  id: string;
  name: string;
  carIds: string[];
}

function bucketsOf(room: MachineRoom, kind: BucketKind): Bucket[] {
  return kind === 'group' ? room.groups : room.hoistways;
}

export function addBucket(room: MachineRoom, kind: BucketKind, id: string): string {
  if (kind === 'group') {
    const group: Group = { id, name: nextGroupName(room), carIds: [], landings: DEFAULT_LANDINGS };
    room.groups.push(group);
    return group.name;
  }
  const hoistway: Hoistway = { id, name: nextHoistwayName(room), carIds: [] };
  room.hoistways.push(hoistway);
  return hoistway.name;
}

/**
 * Only an EMPTY bucket can be removed, and only when it is not the last one.
 * Deleting a populated group would silently unplace its cars and delete the
 * Lobbies section their answers were keyed to.
 */
export function removeBucket(room: MachineRoom, kind: BucketKind, id: string): boolean {
  const buckets = bucketsOf(room, kind);
  const bucket = buckets.find((candidate) => candidate.id === id);
  if (!bucket || bucket.carIds.length > 0) return false;
  if (kind === 'group') room.groups = room.groups.filter((candidate) => candidate.id !== id);
  else room.hoistways = room.hoistways.filter((candidate) => candidate.id !== id);
  return true;
}

/** Spec Q5: a group serves at least one landing; schema.ts pins it positive. */
export const MIN_LANDINGS = 1;
/**
 * D-054: a cap on a typed count. The landings are typeable since D-053, and
 * every landing becomes a row downstream (L4a ranges, FLS back boxes), so a
 * slip like "2000" could stall the phone. The Stepper clamps a typed number to
 * this; `setLandings` holds the same line for any other caller. Not a schema
 * rule: an older record above it still parses.
 */
export const MAX_LANDINGS = 100;
export const DEFAULT_LANDINGS = 2;

export function setLandings(room: MachineRoom, groupId: string, landings: number): void {
  const group = room.groups.find((candidate) => candidate.id === groupId);
  if (group) group.landings = Math.min(MAX_LANDINGS, Math.max(MIN_LANDINGS, Math.round(landings)));
}

/**
 * The tap-to-assign gesture from the mockup: tapping a car puts it in the ACTIVE
 * bucket, tapping it again takes it out, and a car owned by another bucket is
 * not tappable at all (the mockup dims and dashes it). Returns whether anything
 * moved, so a screen never persists a no-op write.
 */
export function toggleCarInBucket(
  room: MachineRoom,
  kind: BucketKind,
  bucketId: string,
  carId: string,
): boolean {
  const buckets = bucketsOf(room, kind);
  const target = buckets.find((candidate) => candidate.id === bucketId);
  if (!target) return false;
  if (!room.cars.some((car) => car.id === carId)) return false;

  if (target.carIds.includes(carId)) {
    target.carIds = target.carIds.filter((id) => id !== carId);
    return true;
  }
  // Owned elsewhere: the control is not tappable, and a stray call must not
  // create the double-membership V7 rejects.
  if (buckets.some((bucket) => bucket.id !== bucketId && bucket.carIds.includes(carId))) {
    return false;
  }
  target.carIds.push(carId);
  target.carIds.sort(carOrder(room));
  return true;
}

/** Keep every car list in the room's own car order, so "Car 2" never follows "Car 10". */
function carOrder(room: MachineRoom): (a: string, b: string) => number {
  const index = new Map(room.cars.map((car, at) => [car.id, at]));
  return (a, b) => (index.get(a) ?? 0) - (index.get(b) ?? 0);
}

export type CarSlotState = 'mine' | 'other' | 'free';

export interface BuilderCar {
  carId: string;
  label: string;
  state: CarSlotState;
  /** The bucket that owns it, when that is not the active one. */
  ownerName?: string;
  onAts: boolean;
}

export interface BuilderTab {
  id: string;
  name: string;
  carCount: number;
  active: boolean;
  /** True when this tab can be removed — empty, and not the only one. */
  removable: boolean;
}

export interface BuilderView {
  kind: BucketKind;
  tabs: BuilderTab[];
  /** The tab taps land in. Undefined only when the room has no buckets yet. */
  activeId?: string;
  activeName?: string;
  /** Groups carry a landings stepper; hoistways do not. */
  landings?: number;
  cars: BuilderCar[];
}

/**
 * Everything the builder needs to draw, resolved from the tree.
 *
 * `activeId` is sticky but self-healing: a screen holds the surveyor's chosen
 * tab in React state, and if that bucket is removed (or the document arrives
 * from a sync with different ones) the view falls back to the first bucket
 * rather than rendering a builder that quietly assigns nothing.
 */
export function builderView(
  room: MachineRoom,
  kind: BucketKind,
  activeId?: string,
): BuilderView {
  const buckets = bucketsOf(room, kind);
  const active =
    buckets.find((bucket) => bucket.id === activeId) ?? buckets[0] ?? undefined;

  const owners = new Map<string, Bucket>();
  for (const bucket of buckets) {
    for (const carId of bucket.carIds) if (!owners.has(carId)) owners.set(carId, bucket);
  }

  const cars: BuilderCar[] = room.cars.map((car) => {
    const owner = owners.get(car.id);
    if (!owner) return { carId: car.id, label: car.label, state: 'free', onAts: car.onAts };
    if (active && owner.id === active.id) {
      return { carId: car.id, label: car.label, state: 'mine', onAts: car.onAts };
    }
    return {
      carId: car.id,
      label: car.label,
      state: 'other',
      ownerName: owner.name,
      onAts: car.onAts,
    };
  });

  const tabs: BuilderTab[] = buckets.map((bucket) => ({
    id: bucket.id,
    name: bucket.name,
    carCount: bucket.carIds.length,
    active: active?.id === bucket.id,
    removable: bucket.carIds.length === 0 && buckets.length > 1,
  }));

  const landings =
    kind === 'group' && active
      ? room.groups.find((group) => group.id === active.id)?.landings
      : undefined;

  return {
    kind,
    tabs,
    ...(active ? { activeId: active.id, activeName: active.name } : {}),
    ...(landings === undefined ? {} : { landings }),
    cars,
  };
}

/**
 * §4 rule 7, straight off the validation engine.
 *
 * The engine raises one issue per problem car ("Car 5 isn't in a group yet."),
 * which is exactly what the surveyor has to act on — so the screen lists those
 * messages verbatim and counts them. Deciding here which cars are unplaced would
 * be a second implementation of the rule the submit gate uses.
 */
export function assignmentIssues(
  results: SurveyResults,
  roomId: string,
  kind: BucketKind,
): ValidationIssue[] {
  const questionId = kind === 'group' ? 'M6' : 'M7';
  return issuesFor(results, questionId, { roomId }).filter((issue) => issue.ruleId === 'V7');
}

/*
 * `builderStatus` — the progress line and remaining-car bullets under each
 * builder — retired with D-030 R11: the chip fill IS the assignment state,
 * and completeness talks once, at Close & Next, through the walk guard.
 * `assignmentIssues` above is what the guard reads.
 */

/* ─────────────────────── changing the room type (M1) ────────────────────── */

export interface TypeChangePlan {
  from: RoomType;
  to: RoomType;
  /** True when the question SET changes — Traction ⇄ Hydro/MRL. */
  switchesQuestionSet: boolean;
  /** Answer keys that would stop applying and be discarded. */
  keys: string[];
  /** Those keys' questions, for the warning the surveyor reads. */
  questions: QuestionDef[];
}

/**
 * What changing a room's type would cost, computed before anything is touched.
 *
 * Only answers whose question does not exist in the destination form set are
 * listed: G11–G17 are Traction-only, E14 and P6 are Hydro-only, and everything
 * else survives the change untouched. MRL runs the Hydro set (spec Q3), so
 * Hydro ⇄ MRL lists nothing — that falls out of `formSetFor()`, not a special
 * case.
 */
export function typeChangePlan(
  doc: SurveyDocument,
  room: MachineRoom,
  to: RoomType,
): TypeChangePlan {
  const nextSet = formSetFor(to);
  const keys: string[] = [];
  const questions: QuestionDef[] = [];

  for (const question of QUESTIONS) {
    if (!question.formSets || question.formSets.includes(nextSet)) continue;
    for (const scope of scopesFor(room, question)) {
      const key = answerKey(question.id, scope);
      if (doc.answers[key] === undefined) continue;
      keys.push(key);
      if (!questions.includes(question)) questions.push(question);
    }
  }

  return {
    from: room.type,
    to,
    switchesQuestionSet: formSetFor(room.type) !== nextSet,
    keys,
    questions,
  };
}

/** Every scope inside this room a question of that level can be answered in. */
function scopesFor(room: MachineRoom, question: QuestionDef): Scope[] {
  switch (question.level) {
    case 'room':
      return [{ roomId: room.id }];
    case 'group':
      return room.groups.map((group) => ({ roomId: room.id, groupId: group.id }));
    case 'hoistway':
      return room.hoistways.map((hoistway) => ({ roomId: room.id, hoistwayId: hoistway.id }));
    case 'building':
    case 'survey':
      // Not this room's to discard.
      return [];
  }
}

/**
 * Apply a type change: set the type, drop exactly the answers the plan named.
 *
 * Mutates the document in place, so it runs inside `setStructure()` and lands in
 * IndexedDB in ONE transaction with the type change. Discarding the answers in a
 * separate write would leave a window in which the room is Hydro while G13 still
 * carries a Traction answer — and a force-quit inside that window would persist
 * it.
 */
export function applyTypeChange(doc: SurveyDocument, roomId: string, to: RoomType): void {
  const room = doc.rooms.find((candidate) => candidate.id === roomId);
  if (!room) return;
  for (const key of typeChangePlan(doc, room, to).keys) delete doc.answers[key];
  room.type = to;
}

/* ──────────────────────── what this setup generates ─────────────────────── */

/*
 * `generationSummary` — "This room will have 12 sections to walk…" — retired
 * with D-030: the map branch shows the rows themselves the moment they
 * exist, which is the fact the sentence restated.
 */

/** The M1 catalog options, in catalog order — the segmented control's source. */
export function roomTypeOptions(): { value: RoomType; label: string }[] {
  const question = byId('M1');
  const options = question?.options ?? [];
  return options.map((option) => ({ value: option.value as RoomType, label: option.label }));
}
